export enum MailTemplateType {
  INTERVIEW_BOOKED_CANDIDATE = 'MW_Interview_Booked_Candidate',
  INTERVIEW_BOOKED_COMPANY = 'MW_Interview_Booked_Company',
  STATUS_CHANGED_ACCEPTED = 'MW_Status_Changed_Accepted',
  STATUS_CHANGED_WAITING = 'MW_Status_Changed_Waiting',
  STATUS_CHANGED_REJECTED = 'MW_Status_Changed_Rejected',
  CANDIDATE_REMINDER = 'MW_Candidate_Reminder',
  CANDIDATE_FEEDBACK = 'MW_Candidate_Feedback',
  COMPANY_REJECTED_BY_USER = 'MW_Company_Rejected_By_User',
  COMPANY_FEEDBACK = 'MW_Company_Feedback',
  COMPANY_USER_ADDED = 'MW_Company_User_Added',
  COMPANY_REGISTRATION_SUBMITTED = 'MW_Company_Registration_Submitted',
  COMPANY_REGISTRATION_REJECTED = 'MW_Company_Registration_Rejected',
  APPLICATION_SUBMITTED = 'MW_Application_Submitted',
}

export enum MailTemplateVars {
  COMPANY_FIRST_NAME = 'companyFirstName',
  COMPANY_LAST_NAME = 'companyLastName',
  /** Greeting-friendly full name with a sensible fallback ("Nutzer:in" if neither name is set). */
  COMPANY_FULL_NAME = 'companyFullName',
  COMPANY_USERNAME = 'companyUsername',
  COMPANY_NAME = 'companyName',
  CANDIDATE_FIRST_NAME = 'candidateFirstName',
  CANDIDATE_LAST_NAME = 'candidateLastName',
  /** Greeting-friendly full name with a sensible fallback ("Nutzer:in" if neither name is set). */
  CANDIDATE_FULL_NAME = 'candidateFullName',
  CANDIDATE_EMAIL = 'candidateEmail',
  JOB_TITLE = 'jobTitle',
  TIME_SLOT = 'timeSlot',
  MEETING_LINK = 'meetingLink',
  BOOK_APPOINTMENT_LINK = 'bookAppointmentLink',
  DAYS_UNTIL_INTERVIEW = 'daysUntilInterview',
  REGISTRATION_COMPANY_NAME = 'registrationCompanyName',
  REGISTRATION_CONTACT_NAME = 'registrationContactName',
  REGISTRATION_CONTACT_EMAIL = 'registrationContactEmail',
  REJECTION_REASON = 'rejectionReason',
  JOB_TITLES = 'jobTitles',
  COMPANY_NAMES = 'companyNames',
  /** Magic-login URL: opens /action/login?token=… and signs the recipient in. */
  LOGIN_LINK = 'loginLink',
}

//* --- HELPER FUNCTIONS ---
/**
 * Build a greeting-friendly name set for any user-bearing mail. Returns
 * `firstName`, `lastName` (both trimmed; empty string if missing) and
 * `fullName` (combined name, falling back to whichever single name is set,
 * else `'Nutzer:in'`). Used by every mail's `getMailData` so we never
 * accidentally substitute a username/email into the salutation line —
 * the prior `?? user.username` fallback produced output like
 * "Hallo --- john@example.com!".
 */
export const buildUserGreeting = (user: { firstName?: unknown; lastName?: unknown }) => {
  const first = ((user.firstName as string | null | undefined) ?? '').trim();
  const last = ((user.lastName as string | null | undefined) ?? '').trim();
  if (first && last) return { firstName: first, lastName: last, fullName: `${first} ${last}` };
  if (first) return { firstName: first, lastName: '', fullName: first };
  if (last) return { firstName: '', lastName: last, fullName: last };
  return { firstName: '', lastName: '', fullName: 'Nutzer:in' };
};

/**
 * Build the `LOGIN_LINK` mail variable value as ready-to-render HTML. The
 * surrounding `<a>` lives in the *variable*, not in the template body — that
 * way the admin mail editor's TinyMCE never sees a placeholder inside an
 * `href` attribute (it would strip the anchor as an invalid URL and leave
 * the URL as plain text). Template authors place a bare `{{loginLink}}`
 * wherever the link should appear.
 */
export const buildLoginLink = (url: string, linkText: string) => {
  return `<a href="${url}">${linkText}</a>`;
};

// used in mails for formatting time slots
export const formatTimeSlot = (start: Date, end: Date) => {
  const deDate = new Intl.DateTimeFormat('de-DE', { timeZone: 'Europe/Berlin', dateStyle: 'short' });
  const deTime = new Intl.DateTimeFormat('de-DE', { timeZone: 'Europe/Berlin', hour: '2-digit', minute: '2-digit' });
  const tzLabel = 'Europe/Berlin';
  if (!start || !end) return '-'; //! needed because we use it also for job application mails where the user has not booked a slot yet

  return `${deDate.format(start)} ${deTime.format(start)} - ${deTime.format(end)} (${tzLabel})`;
};

//* --- MAIL TEMPLATES ---
export const candidateBookingMailTemplate = {
  emailType: MailTemplateType.INTERVIEW_BOOKED_CANDIDATE,
  scope: 'default',
  subject: 'Termin erfolgreich gebucht',
  content: `
<p>Hallo {{candidateFullName}},</p>
<p> Ihr Termin für das Vorstellungsgespräch zur Stelle <b>{{jobTitle}}</b> wurde erfolgreich gebucht.</p>
<p><b>Wann:</b> {{timeSlot}}</p>
<p><b>Meeting-Link:</b> <a href="{{meetingLink}}">LINK</a></p>
<p>Viel Erfolg!</p>
`,
  footer: '',
};

export const companyBookingMailTemplate = {
  emailType: MailTemplateType.INTERVIEW_BOOKED_COMPANY,
  scope: 'default',
  subject: 'Neuer Interview-Termin gebucht',
  content: `
<p>Hallo {{companyFullName}},</p>
<p>Für die Stelle <b>{{jobTitle}}</b> wurde ein Termin gebucht.</p>
<p><b>Kandidat:</b> {{candidateFullName}} ({{candidateEmail}})</p>
<p><b>Wann:</b> {{timeSlot}}</p>
<p><b>Meeting-Link:</b> <a href="{{meetingLink}}">LINK</a></p>
`,
  footer: '',
};

// * Status Change Mails
// Candidate accepted
export const statusChangedAcceptedMailTemplate = {
  emailType: MailTemplateType.STATUS_CHANGED_ACCEPTED,
  scope: 'default',
  subject: 'Einladung zu Bewerbungsgespräch – Ihre Bewerbung wurde akzeptiert',
  content: `
  <p>Hallo {{candidateFullName}},</p>
  <p> Herzlichen Glückwunsch! Ihre Bewerbung für die Stelle <b>{{jobTitle}}</b> wurde akzeptiert.</p>
  <p> Bitte buchen Sie als nächstes einen Termin für das Vorstellungsgespräch unter folgendem Link: <a href="{{bookAppointmentLink}}">LINK</a></p>
  <p>Wir wünschen Ihnen viel Erfolg!</p>
  `,
  footer: '',
};
// Candidate put on waiting list
export const statusChangedWaitingMailTemplate = {
  emailType: MailTemplateType.STATUS_CHANGED_WAITING,
  scope: 'default',
  subject: 'Warteliste – Sie sind auf der Warteliste',
  content: `
    <p>Hallo {{candidateFullName}},</p>
    <p> Sie sind auf der Warteliste für die Stelle <b>{{jobTitle}}</b>.</p>
    <p> Sie bekommen eine Email sobald sich Ihr Bewerbungsstatus ändert.</p>
    <p>Wir wünschen Ihnen weiterhin viel Erfolg!</p>
    `,
  footer: '',
};
// Candidate rejected
export const statusChangedRejectedMailTemplate = {
  emailType: MailTemplateType.STATUS_CHANGED_REJECTED,
  scope: 'default',
  subject: 'Absage – Ihre Bewerbung wurde abgelehnt',
  content: `
<p>Hallo {{candidateFullName}},</p>
<p>Leider hat es dieses Mal nicht geklappt und Ihre Bewerbung für die Stelle <b>{{jobTitle}}</b> wurde abgelehnt.</p>
<p>Wir wünschen Ihnen weiterhin viel Erfolg!</p>
`,
  footer: '',
};

// Company rejected
export const companyRejectedByUserMailTemplate = {
  emailType: MailTemplateType.COMPANY_REJECTED_BY_USER,
  scope: 'default',
  subject: 'Absage – Ein: Kandidat:in hat ein Vorstellungsgespräch abgesagt',
  content: `
<p>Hallo {{companyFullName}},</p>
<p> Leider hat es dieses Mal nicht gepasst und ein:e Kandidat:in hat ein Vorstellungsgespräch abgesagt. </p>
<p><b>Stelle:</b> {{jobTitle}}</p>
<p><b>Kandidat:in :</b> {{candidateFullName}}</p>
<p><b>Wann:</b> {{timeSlot}}</p>

<p>Wir wünschen Ihnen weiterhin viel Erfolg!</p>

`,
  footer: '',
};

//* Candidate Mails
// Sent 3 and 1 day before appointment as a reminder
export const candidateReminderMailTemplate = {
  emailType: MailTemplateType.CANDIDATE_REMINDER,
  scope: 'default',
  subject: 'Erinnerung an Ihr Vorstellungsgespräch',
  content: `
<p>Hallo {{candidateFullName}},</p>
<p> Hiermit erinnern wir Sie an Ihren Termin für das Vorstellungsgespräch zur Stelle <b>{{jobTitle}}</b>.</p>
<p> Noch {{daysUntilInterview}} Tage bis zum Termin.</p>
<p><b>Wann:</b> {{timeSlot}}</p>
<p><b>Meeting-Link:</b> <a href="{{meetingLink}}">LINK</a></p>
<p>Viel Erfolg!</p>
`,
  footer: '',
};

// Sent 1 day after the interview as a feedback request
export const candidateFeedbackMailTemplate = {
  emailType: MailTemplateType.CANDIDATE_FEEDBACK,
  scope: 'default',
  subject: 'Feedback zu Ihrem Vorstellungsgespräch',
  content: `
<p>Hallo {{candidateFullName}},</p>
<p> Gestern hatten Sie ein Vorstellungsgespräch zur Stelle <b>{{jobTitle}}</b>, welches wir Ihnen vermittelt haben.</p>
<p> Wir würden uns sehr über Feedback freuen.</p>
<p>Vielen Dank im Voraus!</p>
`,
  footer: '',
};

//* Company Mails
// Sent the day after interviews took place, asking the company to confirm attendance
export const companyFeedbackMailTemplate = {
  emailType: MailTemplateType.COMPANY_FEEDBACK,
  scope: 'default',
  subject: 'Bitte bestätigen Sie Ihre Vorstellungsgespräche',
  content: `
<p>Hallo {{companyFullName}},</p>
<p>gestern hatten Sie ein oder mehrere Vorstellungsgespräche mit Kandidat:innen, die wir Ihnen über die Matching Week vermittelt haben.</p>
<p>Bitte bestätigen Sie für jedes Gespräch, ob es stattgefunden hat oder nicht. Sie finden die offenen Bestätigungen (Ja/Nein) direkt bei den jeweiligen Bewerbungen:</p>
<p><b>Zu den Bewerbungen:</b> {{loginLink}}</p>
<p>Vielen Dank für Ihre Mithilfe!</p>
`,
  footer: '',
};

// sent when a new user is added to manage a company to that user
export const companyUserAddedMailTemplate = {
  emailType: MailTemplateType.COMPANY_USER_ADDED,
  scope: 'default',
  subject: 'Willkommen bei der Matching Week Plattform',
  content: `
<p>Herzlich willkommen, {{companyFullName}}!</p>
<p>Sie wurden soeben als Benutzer für die Matching Week Plattform freigeschaltet. Sie haben nun Zugriff auf alle Funktionen, um Ihre Stellenangebote zu verwalten und mit Kandidat:innen in Kontakt zu treten.</p>
<p><b>Direkt einloggen:</b> {{loginLink}}</p>
<p>Der Login-Link ist 14 Tage gültig. Sollte er abgelaufen sein, können Sie sich jederzeit über die Funktion "Login mit E-Mail" auf der Login-Seite anmelden. Ihr Nutzername lautet: <b>{{companyUsername}}</b></p>
<p>Bei Fragen stehen wir Ihnen jederzeit gerne zur Verfügung!</p>

<p>Wir freuen uns auf die Zusammenarbeit mit Ihnen!</p>

`,
  footer: '',
};

// sent to admin when a new company registration is submitted
export const companyRegistrationSubmittedMailTemplate = {
  emailType: MailTemplateType.COMPANY_REGISTRATION_SUBMITTED,
  scope: 'default',
  subject: 'Neue Unternehmensregistrierung eingegangen',
  content: `
<p>Hallo,</p>
<p>Ein neues Unternehmen hat sich für die Matching Week Plattform registriert:</p>
<p><b>Unternehmen:</b> {{registrationCompanyName}}</p>
<p><b>Kontaktperson:</b> {{registrationContactName}}</p>
<p><b>E-Mail:</b> {{registrationContactEmail}}</p>
<p>Bitte prüfen Sie die Registrierung im Admin-Dashboard.</p>
`,
  footer: '',
};

// sent to company contact when registration is rejected
export const companyRegistrationRejectedMailTemplate = {
  emailType: MailTemplateType.COMPANY_REGISTRATION_REJECTED,
  scope: 'default',
  subject: 'Ihre Registrierung bei der Matching Week Plattform',
  content: `
<p>Hallo {{registrationContactName}},</p>
<p>vielen Dank für Ihr Interesse an der Matching Week Plattform.</p>
<p>Leider können wir Ihre Registrierung für <b>{{registrationCompanyName}}</b> derzeit nicht berücksichtigen.</p>
<p><b>Begründung:</b> {{rejectionReason}}</p>
<p>Bei Rückfragen stehen wir Ihnen gerne zur Verfügung.</p>
`,
  footer: '',
};

export const applicationSubmittedMailTemplate = {
  emailType: MailTemplateType.APPLICATION_SUBMITTED,
  scope: 'default',
  subject: 'Ihre Bewerbung wurde eingereicht',
  content: `
<p>Hallo {{candidateFullName}},</p>
<p>vielen Dank für Ihre Bewerbung! Folgende Stellen wurden erfolgreich eingereicht:</p>
<p><b>{{jobTitles}}</b></p>
<p>Bei folgenden Unternehmen: <b>{{companyNames}}</b></p>
<p>Sie werden per E-Mail benachrichtigt, sobald sich der Status Ihrer Bewerbung ändert.</p>
<p>Viel Erfolg!</p>
`,
  footer: '',
};
